import { createApi } from '@reduxjs/toolkit/query/react'
import { gql, GraphQLClient } from 'graphql-request'
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query'
import { databaseDateToUtc, generateTuples, sha256 } from '../components/Utilities'

// sends persisted query requests (https://github.com/prisma-labs/graphql-request/issues/269)
 const createPersistedQueryFetch = (fetchImpl) => async(info, init) => {
    const request = {info, init};
    const processor = getRequestProcessor(request);
    const requestWithQueryHash = await processor.addHash(request);
    const requestWithoutQuery = processor.removeQuery(requestWithQueryHash);
  
    // send a request without the query
    const res = await fetchImpl(requestWithoutQuery.info, requestWithoutQuery.init);
    const resCopy = res.clone();
    const body = await res.json();
  
    // if the query was not found in the server, send another request with the query
    if (isPersistedQueryNotFoundError(body)) {
        return fetchImpl(requestWithQueryHash.info, requestWithQueryHash.init);
    }
    else {
        res.json = () => Promise.resolve(body);
        return resCopy;
    }
}
  
function getRequestProcessor(request) {
    const method = (request.init?.method ?? 'GET').toUpperCase();
    const requestProcessor = requestProcessorByMethod[method];

    if (!requestProcessor) {
        throw new Error('Unsupported request method: ' + method);
    }

    return requestProcessor;
}
  
const requestProcessorByMethod = {
    GET: {
        removeQuery: request => {
            const [url, params] = splitUrlAndSearchParams(getRequestInfoUrl(request.info))
            params.delete('query')
            return {
                ...request,
                info: requestInfoWithUpdatedUrl(request.info, `${url}?${params.toString()}`)
            }
        },
        addHash: async request => {
            const [url, params] = splitUrlAndSearchParams(getRequestInfoUrl(request.info))
            const query = params.get('query')

            if (!query) {
                throw new Error('GET request must contain a query parameter');
            }
        
            const hash = await sha256(query);
    
            params.append(
                'extensions',
                JSON.stringify({
                    persistedQuery: {
                        version: 1,
                        sha256Hash: hash
                    }
                })
            )
        
            return {
                ...request,
                info: requestInfoWithUpdatedUrl(
                    request.info,
                    `${url}?${params.toString()}`
                )
            }
        }
    },
    POST: {
        removeQuery: request => {
            if (typeof request.init?.body !== 'string') {
                throw new Error('POST request must contain a body');
            }
      
            const body = JSON.parse(request.init.body);
            const {query, ...bodyWithoutQuery} = body;

            return {
                ...request,
                init: {
                    ...request.init,
                    body: JSON.stringify(bodyWithoutQuery),
                }
            }
        },
        addHash: async request => {
            if (typeof request.init?.body !== 'string') {
                throw new Error('POST request must contain a body');
            }
        
            const body = JSON.parse(request.init.body);
        
            if (typeof body.query !== 'string') {
                throw new Error('POST request body must contain a query');
            }
        
            const hash = await sha256(body.query);
    
            return {
                ...request,
                init: {
                    ...request.init,
                    body: JSON.stringify({
                        ...body,
                        extensions: {
                                persistedQuery: {
                                version: 1,
                                sha256Hash: hash
                            }
                        }
                    })
                }
            }
        }
    }
}
  
function requestInfoWithUpdatedUrl(info, url) {
    return typeof info === 'string'
        ? url
        : {
            ...info,
            url
        }
}

function getRequestInfoUrl(info) {
    return typeof info === 'string'
        ? info
        : info.url
}

function splitUrlAndSearchParams(url) {
    const startOfSearchParams = url.indexOf('?');

    return startOfSearchParams === -1
        ? [url, new URLSearchParams()]
        : [url.slice(0, startOfSearchParams), new URLSearchParams(url.slice(startOfSearchParams))]
}
  
function isPersistedQueryNotFoundError(resBody) {
    return (
        resBody.errors &&
        resBody.errors.length > 0 &&
        resBody.errors.find(err => err.message === 'PersistedQueryNotFound') != null
    )
}

const { API, CAMERAS, WEATHER_CATEGORY, DEBUG } = window.conf

const client = new GraphQLClient(
    API,
    {
        fetch: createPersistedQueryFetch(fetch)
    }
)

export const graphqlApi = createApi({
    baseQuery: graphqlRequestBaseQuery({ 
        client,
        prepareHeaders: (headers, { getState }) => {
            const token = getState().data?.token

            if (typeof token === 'string' && token.length > 0) {
                headers.set('Authorization', `Bearer ${token}`)
            }

            return headers
        }
    }),
    reducerPath: 'graphqlApi',
    endpoints: (builder) => ({
        getDataByCamera: builder.query({
            // queries data of all cameras in descending order to be processed by generateTuples algorithm
            async queryFn(_arg, _queryApi, _extraOptions, graphqlRequestBaseQuery) {

                var subResults = CAMERAS?.map(camera =>
                    graphqlRequestBaseQuery({
                        document: gql`
                            query {
                                getDataByCamera: archivePictures(
                                    where: { featureDetailId: { eq: ${camera.featureDetailId} } }
                                    order: { datum: DESC }
                                ) {
                                    id
                                    datum
                                }
                            }
                        `
                    })
                    .then(res => {
                        if (res.error) {
                            throw new Error(res.error.message)
                        } 
                        return res.data?.getDataByCamera;
                    })
                    .catch(error => {
                        console.error(error)
                        throw new Error(error.message)
                    })
                )

                return { data: subResults ? generateTuples(await Promise.all(subResults.filter(subResult => subResult !== undefined))) : [] }
            }
        }),
        getTagsByCamera: builder.query({
            // queries tags of all cameras individually and exactly by its current timestamp and not only the global timestamp
            async queryFn(_arg, _queryApi, _extraOptions, graphqlRequestBaseQuery) {

                var { archivePictureByCamera } = _arg
                var subResults = archivePictureByCamera?.map((archivePicture, index) => 
                    archivePicture && graphqlRequestBaseQuery({
                        document: gql`
                            query {
                                getTagsByCamera: archivePicturesDetails(
                                    where: {
                                        and: [
                                            { archivePictures: { featureDetailId: { eq: ${CAMERAS[index].featureDetailId} } } }
                                            { archivePictures: { datum: { eq: "${databaseDateToUtc(archivePicture.datum)}" } } }
                                            { kategorie: { neq: "AI" } }
                                        ]
                                    }
                                ) {
                                    id
                                    kategorie
                                    beschreibung
                                    archivePicturesId
                                    weatherArchives {
                                        weatherCode
                                        temp
                                        humidity
                                        windSpeed
                                        archiveType
                                    }
                                }
                            }
                        `,
                    })
                    .then(res => {
                        if (res.error) {
                            throw new Error(res.error.message)
                        } 
                        return res.data?.getTagsByCamera;
                    })
                    .catch(error => {
                        console.error(error)
                        throw new Error(error.message)
                    })
                )

                return { data: subResults ? await Promise.all(subResults.filter(subResult => subResult !== undefined)) : [] }
            }
        }),
        getTags: builder.query({
            // queries tags of single camera between a given timespan
            async queryFn(_arg, _queryApi, _extraOptions, graphqlRequestBaseQuery) {

                var { date, index } = _arg
                var result = 
                    graphqlRequestBaseQuery({
                        document: gql`
                            query {
                                getTags: archivePicturesDetails(
                                    where: {
                                        and: [
                                            { archivePictures: { featureDetailId: { eq: ${CAMERAS[index].featureDetailId} } } }
                                            { archivePictures: { datum: { gte: "${date}T00:00:00.000Z", lte: "${date}T23:59:59.000Z" } } }
                                            { kategorie: { nin: [ "${WEATHER_CATEGORY}", "AI" ] } }
                                        ]
                                    }
                                ) {
                                    archivePicturesId
                                }
                            }
                        `,
                    })
                    .then(res => {
                        if (res.error) {
                            throw new Error(res.error.message)
                        } 
                        return res.data?.getTags;
                    })
                    .catch(error => {
                        console.error(error)
                        throw new Error(error.message)
                    })

                return { data: await Promise.resolve(result) };
          } 
        }),
        searchTagsByCamera: builder.query({
            // queries tags of (multiple) given cameras between a timestamp, category and description
            async queryFn(_arg, _queryApi, _extraOptions, graphqlRequestBaseQuery) {

                var { data } = _arg
                
                const normalizedCategory = (data.category || "").toLowerCase()
                const shouldIncludeVehicles = !data.category || "fahrzeuge".includes(normalizedCategory)
                const shouldIncludePersons = !data.category || "personen".includes(normalizedCategory)

                var subResults = data?.cameras?.map(camera => 
                    graphqlRequestBaseQuery({
                        document: gql`
                            query {
                                searchTagsByCamera: archivePicturesDetails(
                                    where: {
                                        and: [
                                            { kategorie: { contains: "${data.category}" } }
                                            { beschreibung: { contains: "${data.description}" } }
                                            { archivePictures: { datum: { gte: "${data.startDate}T00:00:00.000Z" lte: "${data.endDate}T23:59:59.000Z" } } }
                                            { archivePictures: { featureDetailId: { eq: ${camera.featureDetailId} } } }
                                            { kategorie: { neq: "AI" } }
                                        ]
                                    }
                                ) {
                                    id
                                    kategorie
                                    beschreibung
                                    archivePictures {
                                        datum
                                    }
                                    weatherArchives {
                                        weatherCode
                                        temp
                                        humidity
                                        windSpeed
                                        archiveType
                                    }
                                }
                                ${shouldIncludeVehicles ? 
                                    `vehicleArchivePictures(
                                        where: {
                                            archivePicture: {
                                                and: [
                                                    { featureDetailId: { eq: ${camera.featureDetailId} } }
                                                    { datum: { gte: "${data.startDate}T00:00:00.000Z" lte: "${data.endDate}T23:59:59.000Z" } }
                                                ]
                                            }
                                        }
                                    ) {
                                        id
                                        archivePicture {
                                            datum
                                        }
                                        numberVehicles
                                    }` : ""}
                                ${shouldIncludePersons ? 
                                    `personArchivePictures(
                                        where: {
                                            archivePicture: {
                                                and: [
                                                    { featureDetailId: { eq: ${camera.featureDetailId} } }
                                                    { datum: { gte: "${data.startDate}T00:00:00.000Z" lte: "${data.endDate}T23:59:59.000Z" } }
                                                ]
                                            }
                                        }
                                    ) {
                                        id
                                        archivePicture {
                                            datum
                                        }
                                        numberPersons
                                    }` : ""}
                            }
                        `,
                    })
                    .then(res => {
                        if (res.error) {
                            throw new Error(res.error.message)
                        } 
                        return {
                            featureDetailId: camera.featureDetailId,
                            data: res.data?.searchTagsByCamera,
                            personArchivePictures: res.data?.personArchivePictures,
                            vehicleArchivePictures: res.data?.vehicleArchivePictures
                        }
                    })
                    .catch(error => {
                        console.error(error)
                        throw new Error(error.message)
                    })
                )

                return { data: subResults ? await Promise.all(subResults.filter(subResult => subResult !== undefined)) : [] }
            } 
        }),
        currentWeather: builder.query({
            async queryFn(_arg, _queryApi, _extraOptions, graphqlRequestBaseQuery) {

                const featureDetailId = CAMERAS.find(camera => !!camera.featureDetailId)?.featureDetailId

                if (!featureDetailId) {
                    throw new Error("featureDetailId not found")
                }

                const weatherLatestRes = await graphqlRequestBaseQuery({
                    document: gql`
                        query {
                            weatherLatest(where: { featureDetail: { some: { id: { eq: ${featureDetailId} } } } }) {
                                id
                                dateUpdate
                                temp
                                humidity
                                windSpeed
                                sunrise
                                sunset
                                weatherCode
                                forecastCode1
                                forecastMinTemp1
                                forecastMaxTemp1
                                forecastCode2
                                forecastMinTemp2
                                forecastMaxTemp2
                                forecastCode3
                                forecastMinTemp3
                                forecastMaxTemp3
                            }
                        }
                    `
                })
    
                if (weatherLatestRes.error) {
                    throw new Error(weatherLatestRes.error.message)
                }

                const weatherLatest = weatherLatestRes.data?.weatherLatest?.[0]
    
                if (!weatherLatest || !weatherLatest.id) {
                    throw new Error("weatherLatest not found")
                }
    
                const weatherLatestAlertsRes = await graphqlRequestBaseQuery({
                    document: gql`
                        query {
                            weatherLatestAlerts(
                                where: {
                                    and: [
                                        { weatherLatestId: { eq: ${weatherLatest.id} } }
                                        { dateUpdate: { eq: "${databaseDateToUtc(weatherLatest.dateUpdate)}" } }
                                    ]
                                }
                            ) {
                                event
                                description
                            }
                        }
                    `
                })
    
                if (weatherLatestAlertsRes.error) {
                    throw new Error(weatherLatestAlertsRes.error.message)
                }

                const weatherLatestAlerts = weatherLatestAlertsRes.data?.weatherLatestAlerts || []
                const weatherLatestAlertsWithoutDuplicates = Array.from(new Map(weatherLatestAlerts.map(alert => [alert.event, alert])).values())

                const currentWeatherRes = {
                    data: {
                        ...weatherLatest,
                        weatherLatestAlerts: weatherLatestAlertsWithoutDuplicates
                    }
                }

                DEBUG && console.log("[graphqlApi] currentWeather", currentWeatherRes.data)

                return currentWeatherRes
            }
        }),
        getAccessMonitoring: builder.query({
            async queryFn(_arg, _queryApi, _extraOptions, graphqlRequestBaseQuery) {

                if (!CAMERAS || CAMERAS.length === 0) return { data: { byCameraId: {}, daysByCameraId: {} } }

                try {
                    const subResults = CAMERAS.map(async (camera) => {
                        const id = camera?.id
                        const featureDetailId = camera?.featureDetailId

                        // still create key with empty data if no featureDetailId
                        if (!featureDetailId) return { id, data: [] }

                        // resolve correct featureDetailId via featureDetailIdWithAi
                        const withAiResult = await graphqlRequestBaseQuery({
                            document: gql`
                                query {
                                    featureDetailIdWithAi(id: ${featureDetailId}) {
                                        id
                                    }
                                }
                            `,
                        })

                        if (withAiResult.error) throw new Error(withAiResult.error.message)

                        const resolvedFeatureDetailIdWithAi = withAiResult.data?.featureDetailIdWithAi?.id

                        if (!resolvedFeatureDetailIdWithAi) return { id, data: [] }

                        // check if access monitoring feature is enabled for this id
                        const featureDetailAiResult = await graphqlRequestBaseQuery({
                            document: gql`
                                query {
                                    featureDetailAi(
                                        where: {
                                            and: [{ featureDetailsId: { eq: ${resolvedFeatureDetailIdWithAi} }, aiParameterId: { eq: 26 } }]
                                        }
                                    ) {
                                        value
                                    }
                                }
                            `,
                        })

                        if (featureDetailAiResult.error) throw new Error(featureDetailAiResult.error.message)

                        const isAccessMonitoringEnabled = featureDetailAiResult.data?.featureDetailAi?.[0]?.value === "True"

                        if (!isAccessMonitoringEnabled) return { id, data: [] }

                        // query accessMonitoring with access monitoring enabled id
                        const accessMonitoringResult = await graphqlRequestBaseQuery({
                            document: gql`
                                query {
                                    accessMonitoring(
                                        where: { featureDetailsId: { eq: ${resolvedFeatureDetailIdWithAi} } }
                                        order: { timestamp: DESC }
                                    ) {
                                        id
                                        timestamp
                                        bestImageName
                                        direction
                                        vehicleType { name }
                                        vehicleSubtype { name }
                                    }
                                }
                            `,
                        })

                        if (accessMonitoringResult.error) throw new Error(accessMonitoringResult.error.message)
                              
                        return { id, data: accessMonitoringResult.data?.accessMonitoring || [] }
                    })

                    const resolvedResults = await Promise.all(subResults)

                    // transforms array to object with camera ids as keys
                    const byCameraId = resolvedResults.reduce((acc, cur) => {
                        if (cur?.id === undefined || cur?.id === null) return acc
                        acc[cur.id] = cur.data
                        return acc
                    }, {})

                    // days per camera (YYYY-MM-DD, from UTC timestamp)
                    const daysByCameraId = {}

                    for (const camera of CAMERAS) {
                        const camId = camera?.id
                        if (camId === undefined || camId === null) continue

                        const items = byCameraId[camId] || []
                        const daySet = new Set()

                        for (const item of items) {
                            if (!item?.timestamp) continue
                            const utc = databaseDateToUtc(item.timestamp) // => ...Z
                            const day = utc?.slice(0, 10) // YYYY-MM-DD
                            if (day) daySet.add(day)
                        }

                        daysByCameraId[camId] = Array.from(daySet).sort()
                    }

                    const result = { data: { byCameraId, daysByCameraId } }

                    DEBUG && console.log("[API] getAccessMonitoring", result)

                    return result
                } catch (error) {
                    console.error("[API] getAccessMonitoring", error)
                    return { error: { status: "CUSTOM_ERROR", message: error.message } }
                }
            }
        }),
        addTags: builder.mutation({
            // manipulates tags by array of archivePictureIds
            async queryFn(_arg, _queryApi, _extraOptions, graphqlRequestBaseQuery) {

                var { data } = _arg
                var subResults = data?.archivePicturesIds?.map(archivePicturesId => 
                    graphqlRequestBaseQuery({
                        document: gql`
                            mutation {
                                setArchivePicturesDetails(
                                    id: ${data.id}
                                    archivePicturesId: ${archivePicturesId}
                                    kategorie: "${data.category}"
                                    beschreibung: "${data.description}"
                                ) {
                                    id
                                    archivePicturesId
                                    kategorie
                                    beschreibung
                                }
                            }
                        `,
                    })
                    .then(res => {
                        if (res.error) {
                            throw new Error(res.error.message)
                        } 
                        return res.data?.setArchivePicturesDetails;
                    })
                    .catch(error => {
                        console.error(error)
                        throw new Error(error.message)
                    })
                )
                  
                return { data: subResults ? await Promise.all(subResults.filter(subResult => subResult !== undefined)) : [] }
            }
        }),
        removeTag: builder.mutation({
            // removes single tag by its id
            query: (args) => ({
                document: gql`
                    mutation {
                        removeArchivePicturesDetails(id: ${args.id}) {
                            id
                        }
                    }
                `,
            }),
            transformResponse: (res) => res.removeArchivePicturesDetails
        }),
        addTimelapseJob: builder.mutation({
            query: (args) => ({ // todo: let the user decide wether or not notifyUser is true or false
                document: gql`
                    mutation {
                        addTimelapseJob(
                            featureDetailId: ${args.featureDetailId}
                            timespanStart: "${args.timespanStart}"
                            timespanEnd: "${args.timespanEnd}"
                            height: ${args.height}
                            width: ${args.width}
                            maxLength: ${args.maxLength}
                            userName: "${args.userName}"
                            userMail: "${args.userMail}"
                            language: "${args.language}"
                            notifyUser: true
                        ) {
                            uUID
                        }
                    }
                `,
            }),
            transformResponse: (res) => res.addTimelapseJob
        }),
        cancelTimelapseGeneration: builder.mutation({
            query: (args) => ({
                document: gql`
                    mutation {
                        cancelTimelapseGeneration(uuid: "${args.uUID}") {
                            jobParameter {
                                state
                            }
                        }
                    }
                `,
            }),
            transformResponse: (res) => res.cancelTimelapseGeneration
        }),
        sendSupportEmail: builder.mutation({
            query: (input) => ({
                document: gql`
                    mutation SendSupportEmail($input: SupportEmailInput!) {
                        sendSupportEmail(input: $input) {
                            success
                            message
                        }
                    }
                `,
                variables: { input }
            }),
            transformResponse: (res) => res.sendSupportEmail
        }),
        setNotificationPreference: builder.mutation({
            query: (input) => ({
                document: gql`
                    mutation SetNotificationPreference($input: NotificationPreferenceInput!) {
                        setNotificationPreference(input: $input)
                    }
                `,
                variables: { input }
            }),
            transformResponse: (res) => res.setNotificationPreference
        })
    })
})

export const {
    useCurrentWeatherQuery,
    useGetAccessMonitoringQuery,
    useGetDataByCameraQuery,
    useLazyGetTagsByCameraQuery,
    useLazyGetTagsQuery,
    useLazySearchTagsByCameraQuery,
    useAddTagsMutation,
    useRemoveTagMutation,
    useAddTimelapseJobMutation,
    useCancelTimelapseGenerationMutation,
    useSendSupportEmailMutation,
    useSetNotificationPreferenceMutation
} = graphqlApi